import { z } from 'zod';
import { RoleId } from '../members/role.js';
import { compactPhone, normalizePhoneIdentifier, parseIdentifier } from './identifiers.js';
import { PASSWORD_MAX, PASSWORD_MIN, validatePassword } from './password-policy.js';
// --- atomic identifier shapes ---------------------------------------------
export const Email = z
    .string()
    .trim()
    .toLowerCase()
    .email()
    .max(255, 'Email must be at most 255 characters');
// E.164-leaning: optional leading `+`, 7–15 digits after stripping spaces /
// hyphens / parens. We normalise to digits-only with optional `+`.
export const Phone = z
    .string()
    .trim()
    .max(32)
    .transform((raw) => normalizePhoneIdentifier(raw) ?? compactPhone(raw))
    .pipe(z
    .string()
    .regex(/^\+?\d{7,15}$/, 'Phone must be 7–15 digits, optionally prefixed with +'));
// Username: ASCII letters / digits / `_` / `.` / `-`, 3–32 chars. Must not
// start with a separator. Self-registration NEVER accepts this — only the
// admin-create endpoint and the future "bind a username" endpoint do.
export const Username = z
    .string()
    .trim()
    .regex(/^[a-zA-Z0-9][a-zA-Z0-9._-]{2,31}$/, 'Username must be 3–32 chars: letters, digits, `_`, `.`, `-` (cannot start with separator)');
export const DisplayName = z
    .string()
    .trim()
    .min(1, 'Display name is required')
    .max(80, 'Display name must be at most 80 characters');
// Strict password schema. The full policy lives in `password-policy.ts` and
// is invoked here via superRefine so the contract returns ZodIssues with the
// canonical error code as the issue's `params.code`. Servers re-run the same
// check authoritatively (with extra context: birthdate, identifier fragments).
export const Password = z
    .string()
    .min(PASSWORD_MIN)
    .max(PASSWORD_MAX)
    .superRefine((value, ctx) => {
    const failure = validatePassword(value);
    if (failure !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: failure.message,
            params: { code: failure.code },
        });
    }
});
// --- locale ---------------------------------------------------------------
export const Locale = z.enum(['zh', 'en']);
// Shared refinement: confirmPassword must equal password. Surfaces as a
// `params.code: 'AUTH_PASSWORD_MISMATCH'` issue on `confirmPassword`.
function attachPasswordMatch(shape) {
    return shape.superRefine((val, ctx) => {
        const v = val;
        if (typeof v.password === 'string' &&
            typeof v.confirmPassword === 'string' &&
            v.password !== v.confirmPassword) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['confirmPassword'],
                message: 'Passwords do not match',
                params: { code: 'AUTH_PASSWORD_MISMATCH' },
            });
        }
    });
}
// --- self-registration: discriminated union over identifier type ----------
const RegisterEmailBase = z.object({
    identifierType: z.literal('email'),
    email: Email,
    password: Password,
    confirmPassword: z.string().min(1).max(256),
    displayName: DisplayName,
    locale: Locale.optional(),
});
export const RegisterEmailRequest = attachPasswordMatch(RegisterEmailBase);
const RegisterPhoneBase = z.object({
    identifierType: z.literal('phone'),
    phone: Phone,
    password: Password,
    confirmPassword: z.string().min(1).max(256),
    displayName: DisplayName,
    locale: Locale.optional(),
});
export const RegisterPhoneRequest = attachPasswordMatch(RegisterPhoneBase);
// Discriminated union still keys on `identifierType`. We use the `*Base`
// shapes (without the superRefine wrapper) because `discriminatedUnion`
// only accepts plain ZodObjects; the password-match refinement is then
// re-applied across the union.
export const RegisterRequest = z
    .discriminatedUnion('identifierType', [RegisterEmailBase, RegisterPhoneBase])
    .superRefine((val, ctx) => {
    const v = val;
    if (v.password !== v.confirmPassword) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['confirmPassword'],
            message: 'Passwords do not match',
            params: { code: 'AUTH_PASSWORD_MISMATCH' },
        });
    }
});
// --- login ----------------------------------------------------------------
// Single `identifier` field. Server detects whether it's an email
// (contains `@`), a phone (matches the phone regex), or a username.
// Login does NOT apply the strict password policy — old passwords created
// under a weaker policy must still be able to authenticate.
export const LoginRequest = z.object({
    identifier: z.string().trim().min(1).max(255),
    password: z.string().min(1).max(256),
});
// --- password reset --------------------------------------------------------
export const PasswordResetIdentifier = z
    .string()
    .trim()
    .min(1)
    .max(255)
    .superRefine((raw, ctx) => {
    const parsed = parseIdentifier(raw);
    if (parsed === null || (parsed.kind !== 'email' && parsed.kind !== 'phone')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Enter an email address or phone number',
        });
    }
})
    .transform((raw) => parseIdentifier(raw)?.value ?? raw.trim());
export const RequestPasswordResetRequest = z.object({
    identifier: PasswordResetIdentifier,
});
export const RequestChildPasswordResetRequest = z.object({
    username: Username,
});
export const ResetPasswordRequest = attachPasswordMatch(z.object({
    token: z.string().min(1, 'Reset token is required').max(256),
    password: Password,
    confirmPassword: z.string().min(1).max(256),
}));
export const ResetPasswordWithPhoneRequest = attachPasswordMatch(z.object({
    phone: Phone,
    code: z
        .string()
        .trim()
        .regex(/^\d{6}$/u, 'Verification code must be 6 digits'),
    password: Password,
    confirmPassword: z.string().min(1).max(256),
}));
// --- admin: create user ---------------------------------------------------
// Admin endpoint. Accepts any combination of identifiers; at least one is
// required. This is the ONLY way a username-based account is born.
export const AdminCreateUserRequest = z
    .object({
    email: Email.optional(),
    phone: Phone.optional(),
    username: Username.optional(),
    displayName: DisplayName,
    password: Password,
    confirmPassword: z.string().min(1).max(256),
    role: RoleId.default('member'),
    locale: Locale.optional(),
})
    .superRefine((val, ctx) => {
    if (val.email === undefined &&
        val.phone === undefined &&
        val.username === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one of email, phone, or username is required',
            params: { code: 'AUTH_NO_IDENTIFIER' },
        });
    }
    if (val.password !== val.confirmPassword) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['confirmPassword'],
            message: 'Passwords do not match',
            params: { code: 'AUTH_PASSWORD_MISMATCH' },
        });
    }
});
// --- self-service: change own username ------------------------------------
export const ChangeUsernameRequest = z.object({
    username: Username,
});
// --- self-service: bind / rotate own email / phone -------------------------
export const StartEmailBindRequest = z.object({
    email: Email,
});
export const StartPhoneBindRequest = z.object({
    phone: Phone,
});
export const ConfirmPhoneBindRequest = z.object({
    phone: Phone,
    code: z
        .string()
        .trim()
        .regex(/^\d{6}$/u, 'Verification code must be 6 digits'),
});
// --- admin: email-domain allowlist ----------------------------------------
// Domain alone — no protocol, no path. We lower-case + strip a leading dot.
export const Domain = z
    .string()
    .trim()
    .toLowerCase()
    .transform((raw) => raw.replace(/^\./, ''))
    .pipe(z
    .string()
    .regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/, 'Invalid domain')
    .max(253));
export const EmailDomainAddRequest = z.object({ domain: Domain });
//# sourceMappingURL=requests.js.map